import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import UpgradeAppContent from '../../../components/BottomSheetComponents/UpgradeAppContent'
import { useNewEliteLandingAnalyticsScreen } from '../analytics/NewEliteLandingAnalyticsContext'
import { NewEliteLandingEventId, trackNewEliteUserClicked } from '../analytics/newEliteLandingAnalytics'
import type { EliteLandingPlan } from '../planTypes'
import type { FamilyOnboardingViewProps } from '../pages/family/familyOnboardingViewProps'
import { useEliteBuyCtaExtras } from '../../../components/elite/EliteBuyCtaExtrasContext'

type NewEliteLandingRebalancingPlanSectionProps = {
  numberOfFundsToRebalance: number
  missedGains: number
  selectedPlan: EliteLandingPlan
  familyConfig: FamilyOnboardingViewProps
  isCtaDisabled: boolean
  isForceUpdateRequired?: boolean
  onCtaClick: () => void
}

export function NewEliteLandingRebalancingPlanSection({
  numberOfFundsToRebalance,
  missedGains,
  selectedPlan,
  familyConfig,
  isCtaDisabled,
  isForceUpdateRequired = false,
  onCtaClick
}: NewEliteLandingRebalancingPlanSectionProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const ctaLabel = getRebalancingPlanCtaLabel(selectedPlan)

  if (numberOfFundsToRebalance <= 0) return null

  return (
    <div className='px-5 mt-5'>
      <span className='text-[14px] font-[500] text-[#232323] inline-block'>
        SEBI-registered experts analysed{' '}
        {familyConfig.isFamilyMode && familyConfig.secondaryUserName
          ? `${familyConfig.secondaryUserName}'s`
          : 'your'}{' '}
        <br /> portfolio and found:
      </span>
      <div
        style={{
          backgroundImage: `url('/images/elite-button-bg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        className='bg-white mt-4 border border-[#FCF3EB] rounded-[16px] px-5 py-4'
      >
        <div className={`${missedGains > 0 ? 'text-black' : 'text-orange30'} font-medium`}>
          {numberOfFundsToRebalance} {numberOfFundsToRebalance > 1 ? 'funds' : 'fund'} to rebalance
        </div>
        {missedGains > 0 ? (
          <div className='text-orange30 text-base'>{roundOffAndFormatNumberWithCurrency(missedGains)} missed gains</div>
        ) : null}
      </div>
      <div className='text-grey40 text-13 mt-3'>
        Know which funds to exit and switch to better performing ones, with{' '}
        {familyConfig.isFamilyMode && familyConfig.secondaryUserName
          ? `${familyConfig.secondaryUserName}'s`
          : 'your'}{' '}
        rebalancing plan (reviewed monthly).
      </div>
      <div className='relative z-0 mt-[50px] mb-6'>
        <div
          onClick={() => {
            if (isCtaDisabled) {
              trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.PrimaryCtaDisabled, {
                ...eliteBuyCtaExtras,
                buttonLabel: ctaLabel,
                scrollSection: 'rebalancing_plan'
              })
              return
            }
            if (isForceUpdateRequired) {
              openBottomSheet(<UpgradeAppContent onDismiss={closeBottomSheet} />)
              return
            }
            trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.RebalancingPlanCta, {
              ...eliteBuyCtaExtras,
              buttonLabel: ctaLabel
            })
            onCtaClick()
          }}
          className={`relative bg-[#232323] text-white rounded-xl text-center px-6 py-5 pb-[23px] z-20 font-medium !h-[56px] flex items-center justify-center ${isCtaDisabled ? '!bg-[#D1D6D6] cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {ctaLabel}
        </div>
        <div className='absolute top-[-30px] text-[13px] font-[500] text-[#232323] pt-1 text-center bg-[#F7CB88] rounded-xl inset-0 z-10'>
          {familyConfig.isFamilyMode && familyConfig.secondaryUserName
            ? `${familyConfig.secondaryUserName}'s`
            : 'Your'}{' '}
          personalised rebalancing plan is ready
        </div>
      </div>
    </div>
  )
}

export function getRebalancingPlanCtaLabel(selectedPlan: EliteLandingPlan): string {
  if (selectedPlan === 'yearly') {
    return 'Unlock plan with Elite Yearly'
  }
  return 'Unlock plan with Elite Monthly'
}
